import type { FC, ReactNode } from 'react';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Stack, SvgIcon, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import MailIcon from '@untitled-ui/icons-react/build/esm/Mail01';
import LinkExternalIcon from '@untitled-ui/icons-react/build/esm/LinkExternal01';
import { useNavigate } from 'react-router-dom';
import { RouterLink } from 'src/components/router-link';
import { Seo } from 'src/components/seo';
import { usePageView } from 'src/hooks/use-page-view';
import type { TeamMember } from 'src/data/team';

const Reveal = ({ children, delay = 0 }: { children: ReactNode; delay?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // threshold 0: fire as soon as ANY pixel enters the viewport.
        // A non-zero threshold can never be met by an element taller
        // than the viewport (intersectionRatio caps at viewportH/elH),
        // which made tall mobile sections stay invisible. Once shown,
        // disconnect so it never hides again.
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -80px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.98)',
        filter: visible ? 'blur(0)' : 'blur(6px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}s, filter 0.7s ease ${delay}s`,
        willChange: 'opacity, transform, filter'
      }}
    >
      {children}
    </Box>
  );
};

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .slice(0, 2)
    .join('');

// Brand mentions in bios/highlights auto-link to the right destination.
// Mystic Media Film is this site, so it routes in-app; the others open out.
// Order longest-first so multi-word names win the regex alternation.
const BRAND_LINKS: { match: string; href: string; external: boolean }[] = [
  { match: 'Mystic Media Film', href: '/', external: false },
  { match: 'Creator Terminal', href: 'https://creatorterminal.com', external: true },
  { match: 'MyShowBro', href: 'https://myshowbro.com', external: true }
];

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const BRAND_REGEX = new RegExp(`(${BRAND_LINKS.map((b) => escapeRegExp(b.match)).join('|')})`, 'gi');

const brandFor = (text: string) =>
  BRAND_LINKS.find((b) => b.match.toLowerCase() === text.toLowerCase());

const brandLinkSx: SxProps<Theme> = {
  color: '#2563eb',
  textDecoration: 'underline',
  textDecorationColor: 'rgba(37,99,235,0.35)',
  textUnderlineOffset: '3px',
  fontWeight: 500,
  transition: 'color 0.2s ease, text-decoration-color 0.2s ease',
  '&:hover': { color: '#1d4ed8', textDecorationColor: '#1d4ed8' }
};

// Split a string into plain text + inline brand links. Only the first mention
// of each brand within a given string is linked, so a paragraph that repeats a
// name (e.g. "MyShowBro ... MyShowBro") doesn't become a wall of links.
const renderWithLinks = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  const alreadyLinked = new Set<string>();
  let cursor = 0;
  let key = 0;

  for (const match of text.matchAll(BRAND_REGEX)) {
    const matched = match[0];
    const start = match.index ?? 0;
    const brand = brandFor(matched);
    if (!brand || alreadyLinked.has(brand.match)) continue;
    alreadyLinked.add(brand.match);

    if (start > cursor) nodes.push(text.slice(cursor, start));
    nodes.push(
      brand.external ? (
        <Box
          key={`brand-${key++}`}
          component="a"
          href={brand.href}
          target="_blank"
          rel="noopener noreferrer"
          sx={brandLinkSx}
        >
          {matched}
        </Box>
      ) : (
        <Box
          key={`brand-${key++}`}
          component={RouterLink}
          href={brand.href}
          sx={brandLinkSx}
        >
          {matched}
        </Box>
      )
    );
    cursor = start + matched.length;
  }

  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes.length ? nodes : [text];
};

interface TeamMemberDetailProps {
  member: TeamMember;
}

export const TeamMemberDetail: FC<TeamMemberDetailProps> = ({ member }) => {
  usePageView();
  const navigate = useNavigate();

  // Smart back: if there's a previous page in this tab session, use it.
  // Otherwise fall through to the About page.
  const handleBack = (e: React.MouseEvent) => {
    if (window.history.length > 1) {
      e.preventDefault();
      navigate(-1);
    }
  };

  const socials: { label: string; href: string; icon: typeof MailIcon }[] = [];
  if (member.email) socials.push({ label: 'Email', href: `mailto:${member.email}`, icon: MailIcon });
  if (member.linkedin) socials.push({ label: 'LinkedIn', href: member.linkedin, icon: LinkExternalIcon });
  if (member.twitter) socials.push({ label: 'Twitter / X', href: member.twitter, icon: LinkExternalIcon });
  if (member.instagram) socials.push({ label: 'Instagram', href: member.instagram, icon: LinkExternalIcon });
  if (member.youtube) socials.push({ label: 'YouTube', href: member.youtube, icon: LinkExternalIcon });
  if (member.discord) socials.push({ label: 'Discord', href: member.discord, icon: LinkExternalIcon });
  if (member.website) socials.push({ label: 'Website', href: member.website, icon: LinkExternalIcon });

  const memberUrl = `https://mysticmediafilm.com/about/team/${member.slug}`;
  const sameAs: string[] = [];
  if (member.linkedin) sameAs.push(member.linkedin);
  if (member.twitter) sameAs.push(member.twitter);
  if (member.instagram) sameAs.push(member.instagram);
  if (member.website) sameAs.push(member.website);
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${memberUrl}#person`,
    name: member.name,
    jobTitle: member.role,
    url: memberUrl,
    ...(member.photo ? { image: `https://mysticmediafilm.com${member.photo}` } : {}),
    ...(member.tagline ? { description: member.tagline } : {}),
    ...(member.email ? { email: member.email } : {}),
    worksFor: { '@id': 'https://mysticmediafilm.com/#organization' },
    ...(sameAs.length > 0 ? { sameAs } : {})
  };

  return (
    <>
      <Seo
        title={`${member.name} · Mystic Media Film`}
        description={member.tagline || `${member.name}, ${member.role} at Mystic Media Film.`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(personSchema)}</script>
      </Helmet>

      {/* Hero / Photo + name */}
      <Box
        sx={{
          position: 'relative',
          background:
            'linear-gradient(rgba(10,10,11,0.6), rgba(10,10,11,0.6)), url(/assets/team-hero-bg.webp) center/cover no-repeat, #0a0a0b',
          color: '#ffffff',
          overflow: 'hidden',
          pt: { xs: 10, md: 14 },
          pb: { xs: 10, md: 14 }
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-15%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(37, 99, 235, 0.3) 0%, transparent 65%)',
            filter: 'blur(80px)',
            pointerEvents: 'none'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-25%',
            left: '-10%',
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.26) 0%, transparent 65%)',
            filter: 'blur(90px)',
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Back link */}
          <Reveal>
            <Box
              component={RouterLink}
              href="/about"
              onClick={handleBack}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: 'rgba(255,255,255,0.7)',
                textDecoration: 'none',
                fontFamily: '"Roboto Mono", monospace',
                fontSize: 12,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                mb: { xs: 5, md: 7 },
                transition: 'color 0.2s ease',
                '&:hover': { color: '#ffffff' }
              }}
            >
              <SvgIcon sx={{ fontSize: 16 }}>
                <ArrowLeftIcon />
              </SvgIcon>
              Back to team
            </Box>
          </Reveal>

          <Reveal delay={0.05}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '0.85fr 1fr' },
                gap: { xs: 5, md: 9 },
                alignItems: 'center'
              }}
            >
              {/* Photo / initials */}
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '3 / 4',
                  borderRadius: 4,
                  overflow: 'hidden',
                  background: member.photo
                    ? `url(${member.photo}) center/cover no-repeat`
                    : 'linear-gradient(135deg, #1f2937 0%, #0a0a0b 100%)',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                {!member.photo && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Plus Jakarta Sans", sans-serif',
                        fontSize: 120,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.16)',
                        letterSpacing: '-0.04em'
                      }}
                    >
                      {getInitials(member.name)}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Name + role + tagline */}
              <Stack spacing={3}>
                <Typography
                  sx={{
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.7)'
                  }}
                >
                  {member.role}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: { xs: 44, md: 68 },
                    fontWeight: 700,
                    lineHeight: 1.04,
                    letterSpacing: '-0.035em',
                    color: '#ffffff'
                  }}
                >
                  {member.name}
                </Typography>
                {member.tagline && (
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontSize: { xs: 17, md: 19 },
                      fontWeight: 400,
                      lineHeight: 1.55,
                      color: 'rgba(255,255,255,0.75)'
                    }}
                  >
                    {member.tagline}
                  </Typography>
                )}

                {socials.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 1 }}>
                    {socials.map((s) => (
                      <Box
                        key={s.label}
                        component="a"
                        href={s.href}
                        target={s.href.startsWith('mailto:') ? undefined : '_blank'}
                        rel={s.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 2,
                          py: 1,
                          borderRadius: 999,
                          border: '1px solid rgba(255,255,255,0.18)',
                          color: 'rgba(255,255,255,0.85)',
                          textDecoration: 'none',
                          fontFamily: '"Inter", sans-serif',
                          fontSize: 13,
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'rgba(255,255,255,0.4)',
                            background: 'rgba(255,255,255,0.05)'
                          }
                        }}
                      >
                        <SvgIcon sx={{ fontSize: 15 }}>
                          <s.icon />
                        </SvgIcon>
                        {s.label}
                      </Box>
                    ))}
                  </Box>
                )}
              </Stack>
            </Box>
          </Reveal>
        </Container>
      </Box>

      {/* Bio body */}
      <Box sx={{ background: '#ffffff', py: { xs: 12, md: 16 } }}>
        <Container maxWidth="md">
          <Reveal>
            <Stack spacing={3.5}>
              {member.bio.map((paragraph, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: 17, md: 19 },
                    fontWeight: 400,
                    lineHeight: 1.7,
                    color: '#27272a'
                  }}
                >
                  {renderWithLinks(paragraph)}
                </Typography>
              ))}
            </Stack>
          </Reveal>

          {member.highlights && member.highlights.length > 0 && (
            <Reveal delay={0.05}>
              <Box sx={{ mt: { xs: 8, md: 10 } }}>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 3 }}>
                  <Box sx={{ width: 24, height: 1, background: '#71717a' }} />
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: 11,
                      fontWeight: 500,
                      letterSpacing: 2,
                      textTransform: 'uppercase',
                      color: '#52525b'
                    }}
                  >
                    Highlights
                  </Typography>
                </Stack>
                <Stack spacing={0}>
                  {member.highlights.map((h, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '48px 1fr',
                        gap: 2,
                        py: 2.5,
                        borderTop: i === 0 ? '1px solid #e4e4e7' : 'none',
                        borderBottom: '1px solid #e4e4e7'
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: '"Roboto Mono", monospace',
                          fontSize: 11,
                          fontWeight: 500,
                          color: '#a1a1aa',
                          letterSpacing: 0.6,
                          pt: 0.5
                        }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Inter", sans-serif',
                          fontSize: { xs: 16, md: 17 },
                          fontWeight: 500,
                          lineHeight: 1.5,
                          color: '#0a0a0b'
                        }}
                      >
                        {renderWithLinks(h)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Reveal>
          )}

          {/* Bottom back link, mirrors the top one */}
          <Reveal delay={0.1}>
            <Box sx={{ mt: { xs: 8, md: 12 } }}>
              <Box
                component={RouterLink}
                href="/about"
                onClick={handleBack}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#52525b',
                  textDecoration: 'none',
                  fontFamily: '"Roboto Mono", monospace',
                  fontSize: 12,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#0a0a0b' }
                }}
              >
                <SvgIcon sx={{ fontSize: 16 }}>
                  <ArrowLeftIcon />
                </SvgIcon>
                Back to team
              </Box>
            </Box>
          </Reveal>
        </Container>
      </Box>
    </>
  );
};
